import React, { useEffect, useMemo, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './SamlAuthentication.styles';
import Content from 'studio/components/Content/Content';
import TextField from 'studio/components/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/CloudDownloadRounded';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import services from 'services';
import TextArea from 'studio/components/TextArea';
import { getUserTypesWithAdmin } from 'studio/constants';
import { openPopup } from 'studio/helpers';
import { veezooUrls } from 'config/constants';
import { Controller, useForm } from 'react-hook-form/dist/index.ie11';
import { yupResolver } from '@hookform/resolvers/dist/ie11/yup';
import * as yup from 'yup';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import HelpButton from 'studio/components/HelpButton';
import Progress from 'studio/components/Progress';
import Alert from 'studio/components/Alert';
import PageTitle from 'studio/components/PageTitle';
import classNames from 'classnames';
import VpnKeyIcon from '@mui/icons-material/VpnKeyRounded';
import { trackEvent } from 'utils/eventTracking';
import FormControlLabel from '@mui/material/FormControlLabel';
import { sortBy } from 'utils/arrayUtils';
import UserAttributeMappings, {
  getSchema as getUserAttributeMappingsSchema
} from 'studio/components/UserAttributeMappings';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDownRounded';

const SamlAuthentication = ({ classes, user, languages }) => {
  useEffect(() => {
    trackEvent('Admin SSO SAML Opened');
  }, []);

  const { withLoader, loading } = useLoader();
  const { successMessage, errorMessage, infoMessage } = useMessage();

  // the service provider metadata XML
  const [spMetadata, setSpMetadata] = useState(undefined);
  // the available user roles
  const [roles, setRoles] = useState(null);
  // specifies whether SAML authentication is allowed for the organization
  const [samlAuthenticationIsAllowed, setSamlAuthenticationIsAllowed] = useState(false);
  // the SAML configuration, initialized to 'undefined' while it is loading
  const [samlConfiguration, setSamlConfiguration] = useState(undefined);
  // the new and tested SAML configuration to be saved, should the user chose to do so
  const [newSamlConfiguration, setNewSamlConfiguration] = useState(null);
  // specifies whether to show or hide the dialog displaying the authentication test result
  const [testResultDialogIsOpen, setTestResultDialogIsOpen] = useState(false);
  // the authentication test result
  const [testResult, setTestResult] = useState(null);
  // the popup window in which the test authentication is performed
  const [authenticationWindow, setAuthenticationWindow] = useState(null);
  // specifies whether all attributes from the test result are expanded or collapsed
  const [expandAllAttributes, setExpandAllAttributes] = useState(false);

  // the entity ID and assertion consumer service (ACS) URL of Veezoo as the service provider
  const [spEntityId, spAssertionConsumerServiceUrl] = useMemo(() => {
    const parser = new DOMParser();
    const document = parser.parseFromString(spMetadata, 'text/xml');
    const entityDescriptor = document.getElementsByTagName('md:EntityDescriptor')[0];
    const entityId = entityDescriptor?.getAttribute('entityID');
    const spSsoDescriptor = entityDescriptor?.getElementsByTagName('md:SPSSODescriptor')[0];
    const assertionConsumerService = Array.from(
      spSsoDescriptor?.getElementsByTagName('md:AssertionConsumerService') || []
    ).find(acs => acs.getAttribute('Binding') === 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST');
    const assertionConsumerServiceUrl = assertionConsumerService?.getAttribute('Location');
    return [entityId, assertionConsumerServiceUrl];
  }, [spMetadata]);

  // the user types, including "Admin" if the current user is admin
  const userTypes = getUserTypesWithAdmin(user);

  // the validation rules for the form fields
  const schema = yup.object().shape({
    isEnabled: yup.bool(),
    idpMetadata: yup.string().required(),
    maxAuthenticationLifetime: yup.number().integer().required(),
    userAttributeMappings: getUserAttributeMappingsSchema(user)
  });

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      isEnabled: false,
      idpMetadata: null,
      maxAuthenticationLifetime: null
    },
    // set to false since it causes issues with array fields inside tabs
    shouldUnregister: false
  });
  const { handleSubmit, control, errors, reset, watch, setError, formState } = form;

  // the form data
  const data = watch();
  // extract the needed form state
  const { isDirty } = formState;

  // closes the test result dialog and the authentication window
  const closeTestResultDialog = () => {
    // close the authentication window if it is open
    authenticationWindow?.close();
    setTestResultDialogIsOpen(false);
  };

  // specifies whether the required data is loading
  const isLoading = spMetadata === undefined || !languages || !roles || samlConfiguration === undefined;

  // initially retrieves the user roles and the SAML configuration
  useEffect(() => {
    getSpMetadata();
    getRoles();
    getSamlConfiguration();
  }, []);

  // resets the form whenever the SAML configuration changes
  useEffect(() => {
    reset(samlConfiguration);
  }, [samlConfiguration]);

  // registers the event handler receiving the test result from the authentication window
  useEffect(() => {
    const onTestResult = event => setTestResult(event.detail);
    window.addEventListener('veezoo-saml-testresult', onTestResult);
    return () => window.removeEventListener('veezoo-saml-testresult', onTestResult);
  }, []);

  const getSpMetadata = async () => {
    const response = await services.getServiceProviderMetadata();
    setSpMetadata(response.success ? response.data : null);
  };

  const getRoles = async () => {
    const response = await services.getRoles();
    if (response.success) {
      const roles = sortBy(
        response.data.data.map(role => role.data),
        role => role.name
      );
      setRoles(roles);
    }
  };

  const getSamlConfiguration = async () => {
    const response = await services.getSamlConfiguration();
    const samlConfiguration = response.data?.data || null;
    if (!response.success && response.response.status === 402) {
      setSamlAuthenticationIsAllowed(false);
    } else {
      setSamlAuthenticationIsAllowed(true);
    }
    setSamlConfiguration(samlConfiguration);
  };

  const testSamlConfiguration = async (samlConfiguration, enableSaving = false) => {
    // reset the new SAML configuration
    setNewSamlConfiguration(null);
    // post the configuration to the test endpoint
    const response = await withLoader(services.testSamlConfiguration(samlConfiguration));
    if (response.success) {
      // if we are to save the new SAML configuration, then keep track of it (this is needed since in contrast
      // to 'data', this object conforms to the yup schema, e.g. numbers are actually numbers and not strings)
      if (enableSaving) {
        setNewSamlConfiguration(samlConfiguration);
      }
      // reset the test result
      setTestResult(null);
      // open the test result dialog
      setTestResultDialogIsOpen(true);
      // the response contains the HTML document with the SAML request form
      const html = response.data;
      // create an object URL containing the above HTML document
      const objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      // open the HTML document in a popup to perform the test authentication in
      const authenticationWindow = openPopup(objectUrl, 'Authentication window', 500, 300);
      // keep track of the window
      setAuthenticationWindow(authenticationWindow);
      // if the authentication window got blocked...
      if (!authenticationWindow) {
        // ...set an error test result
        setTestResult({ popupIsBlocked: true });
      }
    } else {
      showError(response.response, 'testing');
    }
  };

  const replaceSamlConfiguration = async newSamlConfiguration => {
    // replace the SAML configuration
    const response = await withLoader(services.replaceSamlConfiguration(newSamlConfiguration));
    if (response.success) {
      // use the SAML configuration returned by the endpoint (just to be safe, should be the same as the one we sent)
      newSamlConfiguration = response.data.data;
      // if the 'isEnabled' flag changed (i.e. if the authentication mode changed) we need to re-authenticate
      const needsReauthentication =
        !!samlConfiguration && newSamlConfiguration.isEnabled !== samlConfiguration.isEnabled;
      // update the SAML configuration: this will trigger a form reset (just to be safe and in sync with the server)
      setSamlConfiguration(newSamlConfiguration);
      successMessage('The SAML configuration has successfully been updated');
      if (needsReauthentication) {
        infoMessage('The page will reload to reauthenticate');
      } else if (newSamlConfiguration.isEnabled) {
        infoMessage('The page will reload to apply the user attribute mappings');
      }
      if (needsReauthentication || newSamlConfiguration.isEnabled) {
        // reload the page after a short delay
        setTimeout(_ => window.location.reload(), 4000);
      }
    } else {
      showError(response.response, 'saving');
    }
  };

  // is called when the server validation of a SAML configuration failed:
  //   - marks form fields that have an error
  //   - displays an error message
  const showError = (errorResponse, action) => {
    const validationErrors = errorResponse.data?.data?.validationErrors;
    // manually set the errors for all invalid fields
    validationErrors?.forEach(error => setError(error.path, { type: 'server' }));
    errorMessage(`There was an error ${action} the SAML configuration`);
  };

  // returns the relative URL for the documentation given the specified hash
  const docsUrl = hash => `/single-sign-on/saml${hash ? `#${hash}` : ''}`;

  // specifies whether to enable saving the SAML configuration after having tested it
  const enableSavingAfterTest =
    // there must be a configuration to save
    !!newSamlConfiguration &&
    // the test must have succeeded
    !!testResult?.user &&
    // if the configuration is enabled, the user must be a creator or admin (else they would lose access to Studio)
    !(newSamlConfiguration?.isEnabled && ![userTypes.admin, userTypes.creator].includes(testResult.user.data.type));

  return (
    <Content loading={isLoading}>
      {!isLoading && (
        <div className={classes.root}>
          <PageTitle icon={VpnKeyIcon}>SAML authentication</PageTitle>
          {!samlAuthenticationIsAllowed && (
            <p>
              Single sign-on with SAML is only available for <b>Enterprise</b> subscription plans. Check our{' '}
              <Link href={veezooUrls.pricing} target="_blank" rel="noreferrer">
                pricing
              </Link>{' '}
              and{' '}
              <Link href={veezooUrls.contact} target="_blank" rel="noreferrer">
                get in touch
              </Link>{' '}
              with us to enable it!
            </p>
          )}
          {samlAuthenticationIsAllowed && (
            <>
              <Typography variant="body1" gutterBottom>
                Configure single sign-on with SAML. Read the{' '}
                <Link href={`${veezooUrls.docs}${docsUrl()}`} target="_blank" rel="noreferrer">
                  documentation
                </Link>{' '}
                for more information on how to configure it.
              </Typography>

              <form autoComplete="off" className={classes.form}>
                <Controller
                  control={control}
                  name="isEnabled"
                  render={({ ref, value }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          inputRef={ref}
                          onChange={event => {
                            const isEnabled = event.target.checked;
                            if (isEnabled) {
                              handleSubmit(samlConfiguration =>
                                testSamlConfiguration({ ...samlConfiguration, isEnabled }, true)
                              )();
                            } else {
                              handleSubmit(samlConfiguration =>
                                replaceSamlConfiguration({ ...samlConfiguration, isEnabled })
                              )();
                            }
                          }}
                          checked={value}
                        />
                      }
                      label={'SAML single sign-on is ' + (value ? 'enabled' : 'disabled')}
                    />
                  )}
                />

                <Typography variant="h2" gutterBottom>
                  Service Provider
                  <HelpButton docsUrl={docsUrl('configure-your-identity-provider')} small style={{ marginLeft: 10 }} />
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Copy the information below and/or download the service provider metadata to set up your identity
                  provider.
                </Typography>

                <TextField
                  label="Entity id"
                  description="The entity id of the service provider (Veezoo)"
                  value={spEntityId}
                  disabled
                  sx={{ marginBottom: '10px' }}
                />
                <TextField
                  label="Assertion consumer service URL"
                  description="The URL of the assertion consumer service (ACS) of the service provider (Veezoo)"
                  value={spAssertionConsumerServiceUrl}
                  disabled
                />
                <div className={classNames(classes.actions, classes.downloadButton)}>
                  <Button
                    component={Link}
                    color="primary"
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    href={services.serviceProviderMetadataUrl}
                  >
                    Download metadata
                  </Button>
                </div>

                <Typography variant="h2" gutterBottom>
                  Identity Provider
                  <HelpButton
                    docsUrl={docsUrl('enter-your-identity-provider-information')}
                    small
                    style={{ marginLeft: 10 }}
                  />
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Configure the identity provider.
                </Typography>

                <Controller
                  as={TextArea}
                  control={control}
                  name="idpMetadata"
                  label="Identity provider metadata"
                  description="The metadata of the identity provider"
                  error={!!errors.idpMetadata}
                  resize="vertical"
                  sx={{ marginBottom: '10px' }}
                />
                <Controller
                  as={TextField}
                  control={control}
                  type="number"
                  name="maxAuthenticationLifetime"
                  label="Maximum authentication lifetime"
                  description="The maximum lifetime of an authentication at the identity provider, specified in seconds"
                  error={!!errors.maxAuthenticationLifetime}
                />

                <Typography variant="h2" gutterBottom>
                  User Attributes
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Configure user attributes based on attributes from the SAML assertion.
                </Typography>

                <UserAttributeMappings
                  user={user}
                  userTypes={userTypes}
                  languages={languages}
                  roles={roles}
                  form={form}
                />

                <div className={classes.actions}>
                  <Button
                    onClick={_ => reset(samlConfiguration)}
                    disabled={loading || !isDirty || testResultDialogIsOpen}
                  >
                    Reset
                  </Button>
                  {data.isEnabled ? (
                    <>
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={loading || !isDirty || testResultDialogIsOpen}
                        onClick={handleSubmit(samlConfiguration => testSamlConfiguration(samlConfiguration, true))}
                      >
                        Test & Save
                      </Button>
                      {user.isAdmin && (
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={handleSubmit(replaceSamlConfiguration)}
                          disabled={loading || !isDirty || testResultDialogIsOpen}
                        >
                          Save
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={loading || testResultDialogIsOpen}
                        onClick={handleSubmit(samlConfiguration => testSamlConfiguration(samlConfiguration))}
                      >
                        Test
                      </Button>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSubmit(replaceSamlConfiguration)}
                        disabled={loading || !isDirty || testResultDialogIsOpen}
                      >
                        Save
                      </Button>
                    </>
                  )}
                  <HelpButton docsUrl={docsUrl('test-and-save-the-saml-configuration')} />
                </div>
              </form>
              <Dialog
                maxWidth="sm"
                fullWidth
                className={classes.testResultDialog}
                open={testResultDialogIsOpen}
                onClose={closeTestResultDialog}
              >
                <DialogTitle>
                  {enableSavingAfterTest ? 'Save the SAML configuration' : 'SAML authentication test'}
                </DialogTitle>
                <DialogContent>
                  <>
                    {!testResult && (
                      <div className={classes.testAuthentication}>
                        Performing an authentication test with the SAML configuration.
                        <Alert severity="info">
                          Please make sure to allow popups and do not close the authentication window
                        </Alert>
                        <Progress />
                      </div>
                    )}
                    {!!testResult?.user && (
                      <>
                        <Alert severity="success">
                          The authentication test with the SAML configuration was successful
                        </Alert>
                        <Typography variant="subtitle1">User attributes</Typography>
                        <p>The following user attributes have been evaluated:</p>
                        <TableContainer>
                          <Table size="small">
                            <TableBody>
                              {!!user.organizationUsesTenants && (
                                <TableRow>
                                  <TableCell>Tenant</TableCell>
                                  <TableCell>
                                    {testResult.tenant.data.name}
                                    {testResult.tenant.data.externalId && ` (${testResult.tenant.data.externalId})`}
                                  </TableCell>
                                </TableRow>
                              )}
                              <TableRow>
                                <TableCell>SAML name ID</TableCell>
                                <TableCell>{testResult.user.data.providerKey}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>{testResult.user.data.type}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>First name</TableCell>
                                <TableCell>{testResult.user.data.firstName}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Last name</TableCell>
                                <TableCell>{testResult.user.data.lastName}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>E-mail</TableCell>
                                <TableCell>{testResult.user.data.email}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Language</TableCell>
                                <TableCell>
                                  {
                                    // find the language to get its name
                                    languages.find(l => l.data.id === testResult.user.data.languageId)?.data.name ||
                                      // fall back to the language id
                                      testResult.user.data.languageId
                                  }
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Roles</TableCell>
                                <TableCell>
                                  {testResult.user.data.roleIds
                                    // find the roles, falling back to role objects containing just the id
                                    .map(id => roles.find(role => role.id === id) || { id })
                                    // sort the roles by name (fallback: id) alphabetically
                                    .sort((r1, r2) => `${r1.name || r1.id}`.localeCompare(`${r2.name || r2.id}`))
                                    // display the role names, falling back to the ids
                                    .map(role => (
                                      <div key={role.id}>{role.name || role.id}</div>
                                    ))}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {!!testResult.attributes && (
                          <>
                            <Typography
                              variant="subtitle1"
                              onClick={() => setExpandAllAttributes(!expandAllAttributes)}
                              style={{ display: 'flex', alignItems: 'center', marginTop: 16, cursor: 'pointer' }}
                            >
                              <span style={{ marginRight: 8 }}>All attributes</span>
                              {expandAllAttributes ? (
                                <KeyboardArrowDownIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowUpIcon fontSize="small" />
                              )}
                            </Typography>
                            <Collapse in={expandAllAttributes}>
                              <p>The following attributes have been provided in the SAML assertion:</p>
                              <TableContainer>
                                <Table size="small">
                                  <TableBody>
                                    {sortBy(Object.entries(testResult.attributes), ([name, _]) => name).map(
                                      ([name, values]) => (
                                        <TableRow key={name}>
                                          <TableCell>{name}</TableCell>
                                          <TableCell>
                                            {values.map((value, index) => (
                                              <div key={index}>{value}</div>
                                            ))}
                                          </TableCell>
                                        </TableRow>
                                      )
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Collapse>
                          </>
                        )}
                        {newSamlConfiguration?.isEnabled &&
                          ![userTypes.admin, userTypes.creator].includes(testResult.user.data.type) && (
                            <div className={classes.warning}>
                              <Alert severity="error">
                                Your updated user is not a <b>{userTypes.creator}</b>. You cannot save the configuration
                                since you would subsequently lose access to Veezoo Studio.
                              </Alert>
                            </div>
                          )}
                      </>
                    )}
                    {!!testResult?.popupIsBlocked && (
                      <>
                        <Alert severity="error">The authentication test with the SAML configuration failed</Alert>
                        <p>
                          The authentication window was blocked by your browser. Please make sure to allow popups for
                          this site and try again.
                        </p>
                      </>
                    )}
                    {!testResult?.popupIsBlocked && testResult?.user === null && (
                      <>
                        <Alert severity="error">
                          The authentication test with the SAML configuration failed{!!testResult?.errorMessage && ':'}
                          {!!testResult?.errorMessage && (
                            <div className={classes.errorMessage}>{testResult.errorMessage}</div>
                          )}
                        </Alert>
                        <p>
                          Please check and correct your configuration and try again. You can also contact us on our{' '}
                          <Link href={veezooUrls.contact} target="_blank" rel="noreferrer">
                            website
                          </Link>{' '}
                          or on{' '}
                          <Link href={veezooUrls.slack} target="_blank" rel="noreferrer">
                            Slack
                          </Link>{' '}
                          for support.
                        </p>
                      </>
                    )}
                  </>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeTestResultDialog}>Close</Button>
                  {enableSavingAfterTest && (
                    <Button
                      autoFocus
                      color="primary"
                      onClick={() => {
                        closeTestResultDialog();
                        replaceSamlConfiguration(newSamlConfiguration);
                      }}
                    >
                      Save
                    </Button>
                  )}
                </DialogActions>
              </Dialog>
            </>
          )}
        </div>
      )}
    </Content>
  );
};

export default withStyles(styles)(SamlAuthentication);
